import { useEffect, type ReactNode } from "react";
import { XIcon } from "../icons";

interface ModalProps {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  readonly footer?: ReactNode;
}

const MAX_WIDTH_CLASS = {
  sm: "md:max-w-sm",
  md: "md:max-w-md",
  lg: "md:max-w-lg",
  xl: "md:max-w-xl",
  "2xl": "md:max-w-2xl",
  "3xl": "md:max-w-3xl",
} as const;

export function Modal({
  title,
  description,
  children,
  onClose,
  maxWidth = "2xl",
  footer,
}: ModalProps) {
  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40 animate-fade-in backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-6"
        onClick={onClose}
      >
        <div
          className={`relative w-full h-full md:h-auto md:max-h-[85vh] ${MAX_WIDTH_CLASS[maxWidth]} bg-surface md:rounded-lg shadow-2xl flex flex-col overflow-hidden animate-slide-up`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="flex items-center justify-between px-6 py-5 border-b border-border shrink-0">
            <div>
              <h2 className="text-lg font-bold text-ink">{title}</h2>
              {description && (
                <p className="text-xs text-ink-muted mt-0.5">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-surface-alt text-ink-muted touch-feedback transition-colors"
              aria-label={`Fermer ${title.toLowerCase()}`}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </header>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto scrollbar-custom">
            <div className="p-6">
              {children}
            </div>
          </div>

          {/* Footer */}
          {footer && (
            <footer className="px-6 py-4 border-t border-border shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 1rem))" }}>
              {footer}
            </footer>
          )}
        </div>
      </div>
    </>
  );
}
